// Copyright (c) 2025 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Container, Header, Button, Table, Icon, Message, Loader, Input, Label, Pagination, Dropdown } from 'semantic-ui-react';
import { templateApi, LOATemplate } from '../api/templateApi';

function TemplateListPage() {
  const [templates, setTemplates] = useState<LOATemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const navigate = useNavigate();

  const loadTemplates = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await templateApi.getAll({
        search: searchTerm || undefined,
        page: currentPage,
        size: pageSize
      });
      setTemplates(data.content);
      setTotalPages(data.totalPages);
      setTotalElements(data.totalElements);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load templates');
    } finally {
      setLoading(false);
    }
  }, [searchTerm, currentPage, pageSize]);

  useEffect(() => {
    loadTemplates();
  }, [loadTemplates]);

  // Reset to first page when search changes
  useEffect(() => {
    setCurrentPage(0);
  }, [searchTerm]);

  const handleDelete = async (id: string, isDefault: boolean) => {
    if (isDefault) {
      setError('Cannot delete the default template');
      return;
    }

    if (!globalThis.confirm('Are you sure you want to delete this template?')) {
      return;
    }

    try {
      await templateApi.delete(id);
      await loadTemplates();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete template');
    }
  };

  const handleDownload = async (id: string, name: string) => {
    try {
      const blob = await templateApi.download(id);
      const url = globalThis.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${name}.pdf`;
      document.body.appendChild(a);
      a.click();
      globalThis.URL.revokeObjectURL(url);
      a.remove();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to download template');
    }
  };

  const formatFileSize = (bytes?: number) => {
    if (!bytes) return 'Unknown';
    const kb = bytes / 1024;
    if (kb < 1024) return `${kb.toFixed(1)} KB`;
    const mb = kb / 1024;
    return `${mb.toFixed(1)} MB`;
  };

  return (
    <Container style={{ marginTop: '2rem' }}>
      <Header as='h1'>
        <Icon name='file pdf' />
        <Header.Content>
          LOA Template Management
          <Header.Subheader>Manage Letter of Authority templates</Header.Subheader>
        </Header.Content>
      </Header>

      {/* Search Bar */}
      <div style={{ marginBottom: '20px', display: 'flex', gap: '10px' }}>
        <Input
          icon='search'
          placeholder='Search templates...'
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          style={{ flex: 1 }}
        />
        <Button primary onClick={() => navigate('/templates/new')}>
          <Icon name='plus' />
          Upload Template
        </Button>
      </div>

      {error && (
        <Message negative>
          <Message.Header>Error</Message.Header>
          <p>{error}</p>
        </Message>
      )}

      {loading ? (
        <Loader active inline='centered'>
          Loading templates...
        </Loader>
      ) : (
        <>
          <Table celled striped>
            <Table.Header>
              <Table.Row>
                <Table.HeaderCell>Name</Table.HeaderCell>
                <Table.HeaderCell>Code</Table.HeaderCell>
                <Table.HeaderCell>Description</Table.HeaderCell>
                <Table.HeaderCell>File Size</Table.HeaderCell>
                <Table.HeaderCell>Version</Table.HeaderCell>
                <Table.HeaderCell>Status</Table.HeaderCell>
                <Table.HeaderCell>Actions</Table.HeaderCell>
              </Table.Row>
            </Table.Header>

            <Table.Body>
              {templates.length === 0 ? (
                <Table.Row>
                  <Table.Cell colSpan='7' textAlign='center'>
                    No templates found
                  </Table.Cell>
                </Table.Row>
              ) : (
                templates.map((template) => (
                  <Table.Row key={template.id}>
                    <Table.Cell>
                      <Header as='h4'>
                        <Icon name='file pdf outline' />
                        {template.name}
                      </Header>
                    </Table.Cell>
                    <Table.Cell><code>{template.code}</code></Table.Cell>
                    <Table.Cell>{template.description || '-'}</Table.Cell>
                    <Table.Cell>{formatFileSize(template.fileSize)}</Table.Cell>
                    <Table.Cell>v{template.version || 1}</Table.Cell>
                    <Table.Cell>
                      {template.isDefault && (
                        <Label color='blue' ribbon>
                          <Icon name='star' />
                          Default
                        </Label>
                      )}
                    </Table.Cell>
                    <Table.Cell>
                      <Button.Group size='small'>
                        <Button
                          icon='download'
                          color='teal'
                          onClick={() => handleDownload(template.id!, template.name)}
                        />
                        <Button
                          icon='edit'
                          color='blue'
                          onClick={() => navigate(`/templates/${template.id}/edit`)}
                        />
                        <Button
                          icon='trash'
                          color='red'
                          onClick={() => handleDelete(template.id!, !!template.isDefault)}
                          disabled={template.isDefault}
                        />
                      </Button.Group>
                    </Table.Cell>
                  </Table.Row>
                ))
              )}
            </Table.Body>
          </Table>

          <div style={{ marginTop: '20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div style={{ color: '#666' }}>
              Showing {currentPage * pageSize + 1}-{Math.min((currentPage + 1) * pageSize, totalElements)} of {totalElements} template{totalElements === 1 ? '' : 's'}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <span>Items per page:</span>
              <Dropdown
                compact
                selection
                value={pageSize}
                options={[
                  { key: 10, text: '10', value: 10 },
                  { key: 20, text: '20', value: 20 },
                  { key: 50, text: '50', value: 50 },
                  { key: 100, text: '100', value: 100 }
                ]}
                onChange={(_e, data) => {
                  setPageSize(data.value as number);
                  setCurrentPage(0); // Reset to first page when changing page size
                }}
              />
              <Pagination
                activePage={currentPage + 1}
                totalPages={totalPages}
                onPageChange={(_e, data) => setCurrentPage((data.activePage as number) - 1)}
                siblingRange={1}
                boundaryRange={0}
                ellipsisItem={null}
                firstItem={null}
                lastItem={null}
                prevItem={{ content: <Icon name='chevron left' />, icon: true }}
                nextItem={{ content: <Icon name='chevron right' />, icon: true }}
              />
            </div>
          </div>
        </>
      )}
    </Container>
  );
}

export default TemplateListPage;
